import { useMemo } from "react";
import ReactFlow, { Background, MarkerType, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { formatAssignmentStatus } from "../../utils/formatAssignmentStatus";
import { getCurrentStageIds } from "../../utils/currentStage";

const STATUS_COLORS = {
  DONE: { border: "#15803D", bg: "#F0FDF4", text: "#14532D" },
  RETURNED: { border: "#C2410C", bg: "#FFF7ED", text: "#7C2D12" },
  IN_PROGRESS: { border: "#4F46E5", bg: "#EEF2FF", text: "#312E81" },
  SUBMITTED: { border: "#A16207", bg: "#FEFCE8", text: "#713F12" },
  PENDING: { border: "#D1D5DB", bg: "#F9FAFB", text: "#6B7280" },
  SKIPPED: { border: "#E5E7EB", bg: "#F9FAFB", text: "#9CA3AF" },
};

function StageNode({ data }) {
  const colors = STATUS_COLORS[data.status] || STATUS_COLORS.PENDING;
  const statusInfo = formatAssignmentStatus(data.status);

  return (
    <div
      className={`workflow-graph-node ${
        data.isCurrent ? "workflow-graph-node--current" : ""
      } ${data.isSelected ? "workflow-graph-node--selected" : ""}`}
      style={{
        borderColor: colors.border,
        backgroundColor: colors.bg,
      }}
      onClick={data.onClick}
    >
      {/* ← ADD THIS — incoming edge handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: colors.border,
          width: 8,
          height: 8,
          border: "2px solid white",
        }}
      />

      <div className="graph-node-header">
        <span
          className="graph-node-status-icon"
          style={{ color: colors.border }}
        >
          {statusInfo.icon}
        </span>
        <span className="graph-node-name" style={{ color: colors.text }}>
          {data.label}
        </span>
      </div>
      {data.assigneeName && (
        <div className="graph-node-assignee">{data.assigneeName}</div>
      )}
      {data.isCurrent && <div className="graph-node-current-indicator" />}

      {/* ← ADD THIS — outgoing edge handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: colors.border,
          width: 8,
          height: 8,
          border: "2px solid white",
        }}
      />
    </div>
  );
}
const nodeTypes = { stageNode: StageNode };

export default function WorkflowGraph({
  project,
  selectedStageId,
  onSelectStage,
}) {
  const stages = useMemo(
    () => project?.workflow?.definition?.stages || [],
    [project?.workflow?.definition?.stages],
  );

  const connections = useMemo(
    () => project?.workflow?.definition?.connections || [],
    [project?.workflow?.definition?.connections],
  );

  const assignments = useMemo(
    () => project?.stageAssignments || [],
    [project?.stageAssignments],
  );

  const currentStageIds = useMemo(
    () => getCurrentStageIds(project),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project?.currentStageIds, project?.currentStageId],
  );

  // stages = project?.workflow?.definition?.stages || [];
  // connections = project?.workflow?.definition?.connections || [];
  // assignments = project?.stageAssignments || [];

  const stageStatusMap = useMemo(() => {
    const map = new Map();
    stages.forEach((stage) => {
      const stageAssignments = assignments
        .filter((assignment) => assignment.stageId === stage.id)
        .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));

      const active = stageAssignments.find((assignment) =>
        ["PENDING", "IN_PROGRESS", "SUBMITTED"].includes(assignment.status),
      );
      const latest = stageAssignments[0] || null;
      const rep = active || latest;

      const assigneeName = rep?.assignee
        ? `${rep.assignee.firstName} ${rep.assignee.lastName}`.trim()
        : "";

      let status = "PENDING";
      if (active) status = active.status;
      else if (latest?.status === "DONE") status = "DONE";
      else if (latest?.status === "SKIPPED") status = "SKIPPED";
      else if (latest?.status === "RETURNED") status = "RETURNED";

      map.set(stage.id, { status, assigneeName });
    });
    return map;
  }, [stages, assignments]);

  const nodes = useMemo(() => {
    return stages.map((stage) => {
      const meta = stageStatusMap.get(stage.id) || {};
      return {
        id: stage.id,
        type: "stageNode",
        position: stage.position || { x: 0, y: 0 },
        sourcePosition: Position.Right, // ← ADD
        targetPosition: Position.Left,
        data: {
          label: stage.name,
          status: meta.status || "PENDING",
          assigneeName: meta.assigneeName || "",
          isCurrent: currentStageIds.includes(stage.id),
          isSelected: stage.id === selectedStageId,
          onClick: () => onSelectStage(stage.id),
        },
        draggable: false,
        selectable: false,
        connectable: false,
      };
    });
  }, [
    stages,
    stageStatusMap,
    currentStageIds,
    selectedStageId,
    onSelectStage,
  ]);

  const edges = useMemo(() => {
    return connections.map((connection) => ({
      id: connection.id,
      source: connection.sourceStageId,
      target: connection.targetStageId,
      type: "smoothstep",
      animated: currentStageIds.includes(connection.targetStageId),
      style: {
        stroke: currentStageIds.includes(connection.targetStageId)
          ? "#4F46E5"
          : "#D1D5DB",
        strokeWidth: currentStageIds.includes(connection.targetStageId)
          ? 2
          : 1.5,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: currentStageIds.includes(connection.targetStageId)
          ? "#4F46E5"
          : "#D1D5DB",
      },
      label: connection.label || "",
      labelStyle: { fontSize: 10, fill: "#9CA3AF" },
    }));
  }, [connections, currentStageIds]);

  if (stages.length === 0) return null;

  return (
    <div className="workflow-graph-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.3,
          includeHiddenNodes: false,
        }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll={false}
        zoomOnPinch
        preventScrolling={false}
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#F3F4F6" gap={20} size={1} variant="dots" />
      </ReactFlow>
    </div>
  );
}
